/**
 * The device's SRP verifier for `ConfirmDevice` (gairm/lms#392; crmarr/templates auth-self-hosted-sign-in.md, R2 and R11).
 *
 * The pool tracks devices, so an API sign-in's refresh token is bound to the device Cognito issued with it
 * (`NewDeviceMetadata`) and renews only once that device is confirmed, with its key on every refresh (session-store.ts,
 * the ordering rule). Confirming it means handing Cognito a verifier for a password only this tab knows — the same
 * construction as SRP-6a's password verifier, with the device group key and device key in place of the pool and the
 * username:
 *
 *   password = 40 random bytes, base64
 *   salt     = 16 random bytes, as a positive integer, padded hex (padHex)
 *   x        = SHA-256( bytes(salt) ‖ bytes( SHA-256(utf8(groupKey ‖ deviceKey ‖ ":" ‖ password)) ) )
 *   verifier = g^x mod N, padded hex — N the RFC 5054 3072-bit group, g = 2
 *
 * sent as `DeviceSecretVerifierConfig: { Salt, PasswordVerifier }`, each the padded hex's bytes in base64.
 *
 * Ported from amazon-cognito-identity-js 6.3.20 (aws-amplify/amplify-js @ 68a0df1145f0f1e79d89753b1aafdbf8836a319d,
 * packages/amazon-cognito-identity-js/src/AuthenticationHelper.js: `initN`, `padHex`, `hash`, `hexHash`,
 * `generateHashDevice`; and CognitoUser.js, where `ConfirmDevice` is called with the result). Apache-2.0. The library's
 * BigInteger and Buffer give way to the platform's BigInt and Web Crypto; nothing else changes. The unit test checks the
 * result against a vector computed by that library with the same inputs (device-verifier.test.ts).
 *
 * The password is what a remembered device's sign-in proves (gairm/lms#400, below): it is kept in the session layer's
 * device slot, beside the tokens, and in the browser's remembered-device entry when the person ticks "Remember this
 * device" (session-store.ts).
 *
 * **The remembered device's sign-in** (gairm/lms#400; R11). A remembered device sends its key in `InitiateAuth`'s
 * `AuthParameters` (`DEVICE_KEY`); after the password Cognito answers `DEVICE_SRP_AUTH` in place of the authenticator
 * code, and the device proves its password by SRP-6a, answered through `RespondToAuthChallenge` (no `ALLOW_USER_SRP_AUTH`
 * on the client):
 *
 *   DEVICE_SRP_AUTH          { USERNAME, DEVICE_KEY, SRP_A }              SRP_A = g^a mod N, a = 128 random bytes
 *   ◀ DEVICE_PASSWORD_VERIFIER { SRP_B, SALT, SECRET_BLOCK }
 *   DEVICE_PASSWORD_VERIFIER { USERNAME, DEVICE_KEY, PASSWORD_CLAIM_SECRET_BLOCK, TIMESTAMP, PASSWORD_CLAIM_SIGNATURE }
 *
 *   k = H(padHex(N) ‖ padHex(g))         u = H(padHex(A) ‖ padHex(B))
 *   x = H(padHex(salt) ‖ H(groupKey ‖ deviceKey ‖ ":" ‖ password))       (the verifier's own x)
 *   S = (B − k·g^x)^(a + u·x) mod N
 *   key = HKDF-SHA256(ikm = padHex(S), salt = padHex(u), info = "Caldera Derived Key" ‖ 0x01), first 16 bytes
 *   signature = base64 HMAC-SHA256(key, utf8(groupKey) ‖ utf8(deviceKey) ‖ base64-decoded SECRET_BLOCK ‖ utf8(TIMESTAMP))
 *   TIMESTAMP = "ddd MMM D HH:mm:ss UTC YYYY", UTC, the day unpadded
 *
 * Ported from the same library: CognitoUser.js `getDeviceResponse`, AuthenticationHelper.js `calculateA`, `calculateU`,
 * `getPasswordAuthenticationKey`, `calculateS`, `computehkdf`, and DateHelper.js `getNowString`. Proven on lms-dev under
 * `USER_AUTH` (gairm/lms#400): the password, then these two challenges, then tokens, with no authenticator code.
 */

/** RFC 5054's 3072-bit group, the prime Cognito's SRP uses (AuthenticationHelper.js `initN`). */
const N_HEX =
  "FFFFFFFFFFFFFFFFC90FDAA22168C234C4C6628B80DC1CD1" +
  "29024E088A67CC74020BBEA63B139B22514A08798E3404DD" +
  "EF9519B3CD3A431B302B0A6DF25F14374FE1356D6D51C245" +
  "E485B576625E7EC6F44C42E9A637ED6B0BFF5CB6F406B7ED" +
  "EE386BFB5A899FA5AE9F24117C4B1FE649286651ECE45B3D" +
  "C2007CB8A163BF0598DA48361C55D39A69163FA8FD24CF5F" +
  "83655D23DCA3AD961C62F356208552BB9ED529077096966D" +
  "670C354E4ABC9804F1746C08CA18217C32905E462E36CE3B" +
  "E39E772C180E86039B2783A2EC07A28FB5C55DF06F4C52C9" +
  "DE2BCBF6955817183995497CEA956AE515D2261898FA0510" +
  "15728E5A8AAAC42DAD33170D04507A33A85521ABDF1CBA64" +
  "ECFB850458DBEF0A8AEA71575D060C7DB3970F85A6E1E4C7" +
  "ABF5AE8CDB0933D71E8C94E04A25619DCEE3D2261AD2EE6B" +
  "F12FFA06D98A0864D87602733EC86A64521F2B18177B200C" +
  "BBE117577A615D6C770988C0BAD946E208E24FA074E5AB31" +
  "43DB5BFCE0FD108E4B82D120A93AD2CAFFFFFFFFFFFFFFFF";
const N = BigInt(`0x${N_HEX}`);
const G = 2n;

/**
 * A non-negative integer as even-length hex, with "00" in front when the top bit is set, so it reads back as positive
 * (AuthenticationHelper.js `padHex`; only the positive branch is ever reached here).
 */
export function padHex(n: bigint): string {
  if (n < 0n) throw new Error("padHex: negative");
  let hex = n.toString(16);
  if (hex.length % 2 !== 0) hex = `0${hex}`;
  return /^[89a-f]/i.test(hex) ? `00${hex}` : hex;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error("hexToBytes: odd length");
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/** SHA-256 as 64 hex characters (AuthenticationHelper.js `hash`). */
async function sha256Hex(data: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", data as BufferSource);
  return bytesToHex(new Uint8Array(digest));
}

/** Square-and-multiply: BigInt has no modPow. The exponent is a SHA-256, so 256 rounds on 3072-bit numbers. */
export function modPow(base: bigint, exponent: bigint, modulus: bigint): bigint {
  let result = 1n;
  let b = base % modulus;
  let e = exponent;
  while (e > 0n) {
    if (e & 1n) result = (result * b) % modulus;
    b = (b * b) % modulus;
    e >>= 1n;
  }
  return result;
}

function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n);
  crypto.getRandomValues(out);
  return out;
}

export interface DeviceVerifier {
  /** The device's own password (base64 of 40 random bytes): kept in the device slot, never sent anywhere. */
  password: string;
  /** What ConfirmDevice takes: the salt's and the verifier's padded-hex bytes, each base64. */
  config: { Salt: string; PasswordVerifier: string };
}

/**
 * The verifier for a device Cognito just issued. `seed` is for the test's known vector only: a fixed password and salt
 * in place of the random ones.
 */
export async function deviceVerifier(groupKey: string, deviceKey: string, seed?: { password: string; saltHex: string }): Promise<DeviceVerifier> {
  const password = seed?.password ?? bytesToBase64(randomBytes(40));
  const saltHex = padHex(BigInt(`0x${seed?.saltHex ?? bytesToHex(randomBytes(16))}`));
  const inner = await sha256Hex(new TextEncoder().encode(`${groupKey}${deviceKey}:${password}`));
  const x = BigInt(`0x${await sha256Hex(hexToBytes(saltHex + inner))}`);
  const verifierHex = padHex(modPow(G, x, N));
  return { password, config: { Salt: bytesToBase64(hexToBytes(saltHex)), PasswordVerifier: bytesToBase64(hexToBytes(verifierHex)) } };
}

/** k = H(padHex(N) ‖ padHex(g)) (AuthenticationHelper.js constructor). */
let kValue: Promise<bigint> | undefined;
function k(): Promise<bigint> {
  return (kValue ??= sha256Hex(hexToBytes(padHex(N) + padHex(G))).then((h) => BigInt(`0x${h}`)));
}

/** The device's half of the handshake: `a`, kept until the answer, and `SRP_A` as Cognito takes it (unpadded hex). */
export interface DeviceSrpStart {
  a: bigint;
  srpA: string;
}

/**
 * `DEVICE_SRP_AUTH`'s `SRP_A` (AuthenticationHelper.js `generateRandomSmallA`, `calculateA`). `aHex` is for the test's
 * known vector only.
 */
export function deviceSrpStart(aHex?: string): DeviceSrpStart {
  const a = BigInt(`0x${aHex ?? bytesToHex(randomBytes(128))}`);
  const A = modPow(G, a, N);
  if (A % N === 0n) throw new Error("SRP: A mod N is zero");
  return { a, srpA: A.toString(16) };
}

/** Cognito's SRP timestamp, "ddd MMM D HH:mm:ss UTC YYYY" (DateHelper.js `getNowString`). */
export function srpTimestamp(at = new Date()): string {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const two = (n: number) => String(n).padStart(2, "0");
  return `${days[at.getUTCDay()]} ${months[at.getUTCMonth()]} ${at.getUTCDate()} ${two(at.getUTCHours())}:${two(at.getUTCMinutes())}:${two(at.getUTCSeconds())} UTC ${at.getUTCFullYear()}`;
}

async function hmacSha256(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const k = await crypto.subtle.importKey("raw", key as BufferSource, { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  return new Uint8Array(await crypto.subtle.sign("HMAC", k, data as BufferSource));
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function concat(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

/** What `DEVICE_PASSWORD_VERIFIER` is answered with, beside `USERNAME` and `DEVICE_KEY`. */
export interface DevicePasswordClaim {
  PASSWORD_CLAIM_SECRET_BLOCK: string;
  PASSWORD_CLAIM_SIGNATURE: string;
  TIMESTAMP: string;
}

/**
 * The device's proof (CognitoUser.js `getDeviceResponse`; AuthenticationHelper.js `getPasswordAuthenticationKey`,
 * `calculateS`, `computehkdf`): from the remembered device's group key, key and password, the handshake's `a`, and
 * Cognito's `SRP_B`, `SALT` and `SECRET_BLOCK`. `timestamp` is for the test's known vector only.
 */
export async function devicePasswordClaim(p: {
  groupKey: string;
  deviceKey: string;
  password: string;
  start: DeviceSrpStart;
  srpB: string;
  salt: string;
  secretBlock: string;
  timestamp?: string;
}): Promise<DevicePasswordClaim> {
  const A = BigInt(`0x${p.start.srpA}`);
  const B = BigInt(`0x${p.srpB}`);
  if (B % N === 0n) throw new Error("SRP: B mod N is zero");
  const u = BigInt(`0x${await sha256Hex(hexToBytes(padHex(A) + padHex(B)))}`);
  if (u === 0n) throw new Error("SRP: u is zero");
  const inner = await sha256Hex(new TextEncoder().encode(`${p.groupKey}${p.deviceKey}:${p.password}`));
  const x = BigInt(`0x${await sha256Hex(hexToBytes(padHex(BigInt(`0x${p.salt}`)) + inner))}`);
  // (B − k·g^x) taken mod N first: the library's BigInteger raises a negative base and reduces after; the same number.
  const base = (((B - (await k()) * modPow(G, x, N)) % N) + N) % N;
  const S = modPow(base, p.start.a + u * x, N);
  const prk = await hmacSha256(hexToBytes(padHex(u)), hexToBytes(padHex(S)));
  const key = (await hmacSha256(prk, concat(new TextEncoder().encode("Caldera Derived Key"), new Uint8Array([1])))).slice(0, 16);
  const timestamp = p.timestamp ?? srpTimestamp();
  const enc = new TextEncoder();
  const signature = await hmacSha256(key, concat(enc.encode(p.groupKey), enc.encode(p.deviceKey), base64ToBytes(p.secretBlock), enc.encode(timestamp)));
  return { PASSWORD_CLAIM_SECRET_BLOCK: p.secretBlock, PASSWORD_CLAIM_SIGNATURE: bytesToBase64(signature), TIMESTAMP: timestamp };
}
